import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Inbox, ShieldAlert, CheckCircle2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

export default function Inbound() {
  const { t, fmtDate } = useI18n();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["inbound"],
    queryFn: async () => (await api.get("/einvoice/inbound")).data,
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/einvoice/inbound", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.duplicate) toast.info(t("einvoice.inbound_duplicate"));
      else if (res.status === "valid") toast.success(t("einvoice.inbound_valid"));
      else toast.warning(t("einvoice.inbound_quarantined"));
      qc.invalidateQueries({ queryKey: ["inbound"] });
    } catch (err) { toast.error(apiErrorMessage(err, t)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <PageHeader title={t("einvoice.inbound_title")} subtitle={t("einvoice.inbound_subtitle")}
        actions={<>
          <input ref={fileRef} type="file" accept=".xml,.pdf,application/xml,application/pdf" className="hidden" onChange={onFile} data-testid="inbound-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="inbound-upload-button"><Upload className="h-4 w-4" />{t("einvoice.upload")}</Button>
        </>} />
      <Card>
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.length === 0 ? (
          <EmptyState icon={Inbox} title={t("einvoice.inbound_empty")} description={t("einvoice.inbound_empty_desc")} testId="inbound-empty" />
        ) : (
          <Table data-testid="inbound-table">
            <TableHeader><TableRow>
              <TableHead>{t("einvoice.inbound_file")}</TableHead>
              <TableHead>{t("einvoice.inbound_syntax")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead>{t("einvoice.inbound_detail")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id} data-testid={`inbound-row-${r.id}`}>
                  <TableCell className="max-w-[240px] truncate font-medium">{r.filename}</TableCell>
                  <TableCell className="uppercase text-xs text-slate-500">{r.syntax || "—"}</TableCell>
                  <TableCell>
                    {r.status === "valid" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" />{t("einvoice.inbound_status_valid")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"><ShieldAlert className="h-4 w-4" />{t("einvoice.inbound_status_quarantined")}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {r.status === "valid"
                      ? [r.summary?.invoice_number, r.summary?.payable_total].filter(Boolean).join(" · ") || "—"
                      : t(`einvoice.quarantine.${r.quarantine_reason}`)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
